import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider
} from "@mui/material";
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  Mail,
  Phone,
  WhatsApp
} from "@mui/icons-material";
import playstore from "../../assets/images/playstore.png";
import appstore from "../../assets/images/appstore.png";
import kanaaLogo from "../../assets/images/kanaalogo1.png";
import { getData } from "../../Webservice/Webservice";

// Cormorant font
const corfont = {
  fontFamily: "'Cormorant', Georgia, serif",
  letterSpacing: "0.4px"
};

const Footer = () => {
  const [footerContact, setFooterContact] = React.useState({
    phone: "+91 9884453499",
    whatsapp: "+91 98844 53499",
    email: "info@kanaadecor.com",
    address: "21, Nerkundram 1st Cross St, Thirunagar, Vadapalani, Chennai, Tamil Nadu 600026.",
    socials: [
      { icon: <Facebook />, href: "https://facebook.com/kanaadecor" },
      { icon: <Instagram />, href: "https://www.instagram.com/kanaa" },
      { icon: <Twitter />, href: "https://twitter.com/kanaadecor" },
      { icon: <LinkedIn />, href: "https://linkedin.com/company/kanaadecor" }
    ]
  });

  React.useEffect(() => {
    const fetchFooter = async () => {
      try {
        const res = await getData("content/contact-address", null, null, false);
        if (res?.data?.content) {
          const c = res.data.content;
          setFooterContact(prev => ({
            ...prev,
            phone: c.phone || prev.phone,
            whatsapp: c.whatsapp || c.phone || prev.whatsapp,
            email: c.email || prev.email,
            address: c.fullAddress || [c.addressLine1, c.addressLine2, c.stateAndPincode, c.country].filter(Boolean).join(", ") || prev.address
          }));
        }
      } catch (err) {
        console.error("Failed to load footer address:", err);
      }
    };
    fetchFooter();
  }, []);
  const brandPurple = "#d793e3ff";
  const darkBg = "#0a0a0a";

  const informationLinks = [
    { text: "Privacy Policy", to: "/policies" },
    { text: "Terms & Conditions", to: "/terms" },
    { text: "Cancellation Policy", to: "/cancellation-policy" },
    { text: "Raising Tickets", to: "/tickets" },
    { text: "Help & Support", to: "/support" }
  ];

  const cleanWhatsApp = (footerContact.whatsapp || "").replace(/[^0-9]/g, "");
  const contactInfo = [
    {
      icon: <Mail />,
      text: footerContact.email,
      href: `mailto:${footerContact.email}`
    },
    { icon: <Phone />, text: footerContact.phone, href: `tel:${footerContact.phone.replace(/\s+/g, "")}` },
    ...(cleanWhatsApp
      ? [
          {
            icon: <WhatsApp sx={{ color: "#25D366" }} />,
            text: `WhatsApp: ${footerContact.whatsapp}`,
            href: `https://wa.me/${cleanWhatsApp}?text=${encodeURIComponent("Hi Kanaa Decor, I would like to enquire about your event decoration services.")}`,
            isExternal: true
          }
        ]
      : []),
    {
      icon: null,
      text: footerContact.address,
      href: "#"
    }
  ];

  const socialLinks = [
    { icon: <Facebook />, href: "https://facebook.com/kanaadecor" },
    {
      icon: <Instagram />,
      href: "https://www.instagram.com/kanaa"
    },
    { icon: <Twitter />, href: "https://twitter.com/kanaadecor" },
    { icon: <LinkedIn />, href: "https://linkedin.com/company/kanaadecor" }
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: darkBg,
        color: "#e0e0e0",
        py: { xs: 6, md: 5 },
        position: "relative",
        width: "100vw",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        ...corfont
      }}
    >
      <Box
        sx={{
          maxWidth: "1400px",
          mx: "auto",
          px: { xs: 3, sm: 4, md: 6, lg: 8 }
        }}
      >
        <Grid container spacing={{ xs: 6, md: 5 }} alignItems="start">
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src={kanaaLogo}
              alt="Kanaa Logo"
              sx={{ height: 60, width: 160 }}
            />

            <Typography
              variant="body1"
              sx={{
                ...corfont,
                fontSize: { xs: "1.1rem", md: "1.2rem" },
                lineHeight: 1.85,
                color: "#b0b0b0",
                maxWidth: "400px"
              }}
            >
              Elevating every celebration with bespoke, soulful decor that
              speaks elegance and emotion.
            </Typography>

            {/* Social Icons */}
            <Box sx={{ display: "flex", gap: 1.8, mt: 4 }}>
              {socialLinks.map((social, i) =>
                <IconButton
                  key={i}
                  component="a"
                  href={social.href}
                  target="_blank"
                  rel="noopener"
                  sx={{
                    bgcolor: "rgba(142, 36, 170, 0.15)",
                    color: brandPurple,
                    width: 48,
                    height: 48,
                    transition: "all 0.35s ease",
                    "&:hover": {
                      bgcolor: brandPurple,
                      color: "#fff",
                      transform: "translateY(-4px) scale(1.1)",
                      boxShadow: "0 8px 20px rgba(142, 36, 170, 0.4)"
                    }
                  }}
                >
                  {social.icon}
                </IconButton>
              )}
            </Box>
          </Grid>

          {/* Information Links */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography
              variant="h6"
              sx={{
                ...corfont,
                fontWeight: 600,
                fontSize: "1.35rem",
                color: "#fff",
                mb: 3
              }}
            >
              Information
            </Typography>
            <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
              {informationLinks.map(link =>
                <Box component="li" key={link.text} sx={{ mb: 2 }}>
                  <Link
                    component={RouterLink}
                    to={link.to}
                    underline="none"
                    sx={{
                      ...corfont,
                      color: "#c0c0c0",
                      fontSize: "1.1rem",
                      transition: "all 0.3s",
                      "&:hover": {
                        color: brandPurple,
                        paddingLeft: "8px"
                      }
                    }}
                  >
                    {link.text}
                  </Link>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Contact Info */}
          <Grid item xs={6} sm={4} md={3}>
            <Typography
              variant="h6"
              sx={{
                ...corfont,
                fontWeight: 600,
                fontSize: "1.35rem",
                color: "#fff",
                mb: 3
              }}
            >
              Get in Touch
            </Typography>
            {contactInfo.map((item, i) =>
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 2.8,
                  fontSize: "1.1rem",
                  width: "250px"
                }}
              >
                {item.icon &&
                  <Box sx={{ color: "#d793e3ff", minWidth: 26 }}>
                    {item.icon}
                  </Box>}
                <Link
                  href={item.href}
                  target={item.isExternal ? "_blank" : undefined}
                  rel={item.isExternal ? "noopener noreferrer" : undefined}
                  color="inherit"
                  underline="none"
                  sx={{
                    ...corfont,
                    color: "#c0c0c0",
                    transition: "color 0.3s",
                    "&:hover": { color: brandPurple }
                  }}
                >
                  {item.text}
                </Link>
              </Box>
            )}
          </Grid>

          {/* App Download */}
          <Grid item xs={12} md={3}>
            <Typography
              variant="h6"
              sx={{
                ...corfont,
                fontWeight: 600,
                fontSize: "1.35rem",
                color: "#fff",
                mb: 3
              }}
            >
              Download Our App
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Link href="https://play.google.com/store/apps" target="_blank">
                <Box
                  component="img"
                  src={playstore}
                  alt="Google Play"
                  sx={{
                    height: { xs: 52, md: 60 },
                    borderRadius: 2,
                    transition: "all 0.3s",
                    "&:hover": {
                      transform: "scale(1.06)",
                      boxShadow: "0 8px 25px rgba(142, 36, 170, 0.3)"
                    }
                  }}
                />
              </Link>
              <Link href="https://www.apple.com/app-store/" target="_blank">
                <Box
                  component="img"
                  src={appstore}
                  alt="App Store"
                  sx={{
                    height: { xs: 52, md: 60 },
                    borderRadius: 2,
                    transition: "all 0.3s",
                    "&:hover": {
                      transform: "scale(1.06)",
                      boxShadow: "0 8px 25px rgba(142, 36, 170, 0.3)"
                    }
                  }}
                />
              </Link>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3, borderColor: "#333" }} />
      </Box>
      {/* Copyright */}
      <Box sx={{ textAlign: "center" }}>
        <Typography
          variant="body2"
          sx={{
            ...corfont,
            fontSize: { xs: "1rem", md: "1.05rem" },
            color: "#888",
            letterSpacing: "0.6px"
          }}
        >
          © {new Date().getFullYear()} Kanaa Decor • Made with passion in India
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;
